import { useState, useCallback, useEffect } from 'react';
import { useAPI } from './useAPI';
import { authService, otpService } from '../services';

export const useOwnerOtpVerification = (login) => {
    const { execute } = useAPI();

    const [showOtpModal, setShowOtpModal] = useState(false);
    const [loginId, setLoginId] = useState('');
    const [password, setPassword] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [otpError, setOtpError] = useState('');

    const openOtpFlow = useCallback((id, userPassword) => {
        setLoginId(id);
        setPassword(userPassword);
        setOtpError('');
        setShowOtpModal(true);
    }, []);

    const handleSendOtp = useCallback(async () => {
        if (!loginId) return;

        setIsSendingOtp(true);
        setOtpError('');
        try {
            await execute(() => otpService.sendOTPSelf(loginId), true, false);
        } catch (error) {
            setOtpError(error?.message || 'Failed to send OTP. Please try again.');
        } finally {
            setIsSendingOtp(false);
        }
    }, [loginId, execute]);

    useEffect(() => {
        if (showOtpModal && loginId) {
            handleSendOtp();
        }
    }, [showOtpModal, loginId, handleSendOtp]);

    const completeLogin = useCallback(async (id, userPassword) => {
        const loginResponse = await execute(() =>
            authService.login({ loginId: id, password: userPassword })
        );

        if (loginResponse.data?.user && loginResponse.data?.token) {
            login(loginResponse.data.user, loginResponse.data.token);
            setShowOtpModal(false);
            setLoginId('');
            setPassword('');
            setOtpError('');
        }
    }, [execute, login]);

    const handleOtpVerify = useCallback(async (otp) => {
        setIsVerifying(true);
        setOtpError('');
        try {
            await execute(() => otpService.verifyOTPSelf(loginId, otp), true, false);
            await completeLogin(loginId, password);
        } catch (error) {
            setOtpError(error?.message || 'OTP verification failed. Please try again.');
        } finally {
            setIsVerifying(false);
        }
    }, [loginId, password, execute, completeLogin]);

    const handleOtpClose = useCallback(() => {
        setShowOtpModal(false);
        setLoginId('');
        setPassword('');
        setOtpError('');
    }, []);

    return {
        showOtpModal,
        loginId,
        isVerifying,
        isSendingOtp,
        otpError,
        openOtpFlow,
        handleSendOtp,
        handleOtpVerify,
        handleOtpClose,
    };
};

export default useOwnerOtpVerification;
